/**
 * The dashboard's one event-stream client (design 11 §4, design 08 §5): `WS /v1/events/stream`
 * through the BFF, with the types and call it follows. When the connection drops it reconnects
 * (1 s, 2 s, 4 s … up to 15 s) and resumes `since` the last event it had, so nothing in between is
 * lost. It pings every 25 s to keep idle proxies from closing it.
 */
import type { Bff } from "./client";

export interface StreamedEvent {
  id: string;
  type: string;
  subject: string;
  time?: string | null;
  swaraloseq: number;
  data: Record<string, unknown>;
}

export type StreamState = "connecting" | "live" | "reconnecting" | "closed";

export interface EventStreamOptions {
  types?: string[];
  callId?: string;
  /** Resume after this event (the last one already shown). */
  since?: string;
  onEvent: (event: StreamedEvent) => void;
  onState?: (state: StreamState) => void;
  /** Times for tests; the defaults are the real ones. */
  delays?: { backoffMs: (attempt: number) => number; pingMs: number };
}

/** Codes after which reconnecting cannot help: the filters are refused, or the user may not read. */
const FINAL = new Set([4400, 4401, 4403]);

export function openEventStream(bff: Bff, options: EventStreamOptions): { close: () => void } {
  const backoff = options.delays?.backoffMs ?? ((n: number) => Math.min(15_000, 1000 * 2 ** n));
  const pingMs = options.delays?.pingMs ?? 25_000;
  let last = options.since;
  let attempt = 0;
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let ping: ReturnType<typeof setInterval> | null = null;
  let closed = false;

  const path = () => {
    const q = new URLSearchParams();
    if (options.types?.length) q.set("types", options.types.join(","));
    if (options.callId) q.set("call_id", options.callId);
    if (last) q.set("since", last);
    const s = q.toString();
    return `/v1/events/stream${s ? `?${s}` : ""}`;
  };

  const connect = () => {
    options.onState?.(attempt === 0 ? "connecting" : "reconnecting");
    const ws = bff.openStream(path());
    socket = ws;
    ws.onopen = () => {
      attempt = 0;
      options.onState?.("live");
      ping = setInterval(() => ws.readyState === 1 && ws.send(JSON.stringify({ op: "ping" })), pingMs);
    };
    ws.onmessage = (m) => {
      if (typeof m.data !== "string") return;
      const message = JSON.parse(m.data) as StreamedEvent & { op?: string };
      if (message.op) return; // pong, error
      last = message.id;
      options.onEvent(message);
    };
    ws.onclose = (c) => {
      if (ping) clearInterval(ping);
      if (closed) return;
      if (FINAL.has(c.code)) {
        options.onState?.("closed");
        return;
      }
      if (c.code === 4410) last = undefined; // the resume point is gone: start from now
      options.onState?.("reconnecting");
      timer = setTimeout(connect, backoff(attempt++));
    };
  };

  connect();
  return {
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      if (ping) clearInterval(ping);
      socket?.close(1000);
      options.onState?.("closed");
    },
  };
}
